import ky from 'ky';

import {getOptions} from '~api/api';
import {NewOrExistingLabelItem, UnsavedLabelItem} from '~redux/types/images';
import {InspectionSubType, InspectionType, Model, ModelCounts} from '~redux/types/models';

const AUTH_API = import.meta.env.VITE_AUTH_API ?? '';
const modelsEndpoint = `${AUTH_API}/models`;

export type ModelsResponse = Model[];
export type SingleModelResponse = Model;
/** V2 model list — server omits per-model counts which arrive via `getModelsCounts`. */
export type ModelsV2Response = Model[];
export type ModelCountsResponse = ModelCounts[];

export type CreateModelPayload = {
  name: string;
  inspectionType: InspectionType;
  inspectionSubtype?: InspectionSubType;
  labels: UnsavedLabelItem[];
};

export type UpdateModelPayload = {
  id: string;
  name: string;
  labels: NewOrExistingLabelItem[];
};

export type CloneModelLabelPayload = {
  oldLabelId: string;
  newLabelAlias: string;
  newLabelColorCode: string;
};

export type CloneModelPayload = {
  newModelName: string;
  labels: CloneModelLabelPayload[];
  uiContext: string;
};

export const getModels = (projectId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(`${modelsEndpoint}/${projectId}`, {
      ...getOptions({signalId: 'getModels', reactQuerySignal}),
      parseJson: (text) => {
        let parseableText = text.replace(/([:\\[,]\s*)-Infinity\b/g, '$1"-inf"');
        parseableText = parseableText.replace(/([:\\[,]\s*)Infinity\b/g, '$1"inf"');
        parseableText = parseableText.replace(/([:\\[,]\s*)NaN\b/g, '$1"nan"');

        return JSON.parse(parseableText);
      },
    })
    .json<ModelsResponse>();
};

/**
 * V2 model list. Excludes per-model counts which are served by `getModelsCounts`. Big-defect-book
 * projects no longer pay the count-aggregation cost on every model-list fetch.
 */
export const getModelsV2 = (projectId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(`${modelsEndpoint}/v2/${projectId}`, {
      ...getOptions({signalId: 'getModelsV2', reactQuerySignal}),
      parseJson: (text) => {
        let parseableText = text.replace(/([:\\[,]\s*)-Infinity\b/g, '$1"-inf"');
        parseableText = parseableText.replace(/([:\\[,]\s*)Infinity\b/g, '$1"inf"');
        parseableText = parseableText.replace(/([:\\[,]\s*)NaN\b/g, '$1"nan"');

        return JSON.parse(parseableText);
      },
    })
    .json<ModelsV2Response>();
};

/**
 * Per-model image/annotation/defect/label-condition counts. Decoupled from the main model list so
 * counts can be fetched in parallel and surfaced progressively as they arrive.
 */
export const getModelsCounts = (projectId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(`${modelsEndpoint}/${projectId}/counts`, getOptions({signalId: 'getModelsCounts', reactQuerySignal}))
    .json<ModelCountsResponse>();
};

export const postModel = (projectId: string, newModel: CreateModelPayload, reactQuerySignal?: AbortSignal) => {
  return ky
    .post(`${modelsEndpoint}/${projectId}`, getOptions({signalId: 'postModel', reactQuerySignal, data: newModel}))
    .json<ModelsResponse>();
};

export const postCloneModel = ({
  projectId,
  modelId,
  payload,
  reactQuerySignal,
}: {
  projectId: string;
  modelId: string;
  payload: CloneModelPayload;
  reactQuerySignal?: AbortSignal;
}) => {
  return ky
    .post(
      `${modelsEndpoint}/${projectId}/${modelId}/clone`,
      getOptions({signalId: 'postCloneModel', reactQuerySignal, data: payload, timeoutMs: 5 * 60 * 1000}),
    )
    .json<Model>();
};

export const putModel = (projectId: string, updatedModel: UpdateModelPayload, reactQuerySignal?: AbortSignal) => {
  return ky
    .put(
      `${modelsEndpoint}/${projectId}/${updatedModel.id}`,
      getOptions({signalId: 'putModel', reactQuerySignal, data: updatedModel}),
    )
    .json<SingleModelResponse>();
};

export const deleteModelLabel = (
  projectId: string,
  modelId: string,
  labelId: string,
  reactQuerySignal?: AbortSignal,
) => {
  return ky
    .delete(
      `${modelsEndpoint}/${projectId}/${modelId}/${labelId}`,
      getOptions({signalId: 'deleteModelLabel', reactQuerySignal}),
    )
    .json<SingleModelResponse>();
};

export const deleteModel = (projectId: string, modelId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .delete(`${modelsEndpoint}/${projectId}/${modelId}`, getOptions({signalId: 'deleteModelLabel', reactQuerySignal}))
    .json<Model[]>();
};

export const getModelTrainingConfig = (projectId: string, modelId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(`${modelsEndpoint}/${projectId}/${modelId}/training_config`, {
      ...getOptions({
        signalId: 'getModelTrainingConfig',
        reactQuerySignal,
        customHeaders: {
          'X-Snake-Case-Resp': 'true',
        },
      }),
      parseJson: (text) => {
        let parseableText = text.replace(/([:\\[,]\s*)-Infinity\b/g, '$1"-inf"');
        parseableText = parseableText.replace(/([:\\[,]\s*)Infinity\b/g, '$1"inf"');
        parseableText = parseableText.replace(/([:\\[,]\s*)NaN\b/g, '$1"nan"');

        return JSON.parse(parseableText);
      },
    })
    .json<ModelTrainingConfigurationResponse>();
};

export const postModelTrainingConfig = (
  projectId: string,
  modelId: string,
  newTrainingConfig: ModelTrainingConfigurationPayload,
  reactQuerySignal?: AbortSignal,
) => {
  return ky
    .post(
      `${modelsEndpoint}/${projectId}/${modelId}/training_config_fe`,
      getOptions({
        signalId: 'postTrainingConfig',
        reactQuerySignal,
        data: newTrainingConfig,
        customHeaders: {
          'X-Snake-Case-Resp': 'true',
        },
      }),
    )
    .json<ModelTrainingConfigurationResponse>();
};

/**
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const postUnpublishModel = ({projectId, modelId}: AutoMlModelArgs) => {
  return ky
    .post(
      `${modelsEndpoint}/${projectId}/${modelId}/unpublish`,
      getOptions({
        signalId: 'postUnpublishModel',
      }),
    )
    .json<{success: boolean}>();
};

export const putArchiveModel = ({projectId, modelId}: {projectId: string; modelId: string}) => {
  return ky
    .put(
      `${modelsEndpoint}/${projectId}/${modelId}/archive`,
      getOptions({
        signalId: 'putArchiveModel',
      }),
    )
    .json<{success: boolean}>();
};

export const putUnarchiveModel = ({projectId, modelId}: {projectId: string; modelId: string}) => {
  return ky
    .put(
      `${modelsEndpoint}/${projectId}/${modelId}/unarchive`,
      getOptions({
        signalId: 'putUnarchiveModel',
      }),
    )
    .json<{success: boolean}>();
};

export const getAdvancedConfiguration = ({projectId, modelId}: {projectId: string; modelId: string}) => {
  return ky
    .get(
      `${modelsEndpoint}/${projectId}/${modelId}/advanced_config`,
      getOptions({
        signalId: 'getAdvancedConfiguration',
        customHeaders: {
          'X-Snake-Case-Resp': 'true',
        },
      }),
    )
    .json<ModelAdvancedConfigurationResponse>();
};

export const postAdvancedConfiguration = ({
  projectId,
  modelId,
  advancedConfig,
}: {
  projectId: string;
  modelId: string;
  advancedConfig: ModelAdvancedConfigurationPayload;
}) => {
  return ky
    .post(
      `${modelsEndpoint}/${projectId}/${modelId}/advanced_config`,
      getOptions({
        signalId: 'postAdvancedConfiguration',
        data: advancedConfig,
        customHeaders: {
          'X-Snake-Case-Resp': 'true',
        },
      }),
    )
    .json<ModelAdvancedConfigurationResponse>();
};

/**
 * Get the selection metric for a model
 * @throws {HTTPError} with content {@link ApiErrorResponse}
 */
export const getModelSelectionMetric = (projectId: string, modelId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(
      `${modelsEndpoint}/${projectId}/${modelId}/selection_metrics`,
      getOptions({
        signalId: 'getModelSelectionMetric',
        reactQuerySignal,
      }),
    )
    .json<SelectionMetricResponse>();
};

export const postPreprocessingPreview = ({
  projectId,
  modelId,
  preprocessing,
}: {
  projectId: string;
  modelId: string;
  preprocessing: JsonObject;
}) => {
  return ky
    .post(
      `${modelsEndpoint}/${projectId}/${modelId}/preprocessing_preview`,
      getOptions({signalId: 'postPreprocessingPreview', data: {preprocessing}}),
    )
    .json<PreprocessingPreviewResponse>();
};

export const postAugmentationPreview = ({
  projectId,
  modelId,
  augmentation,
}: {
  projectId: string;
  modelId: string;
  augmentation: JsonObject[];
}) => {
  return ky
    .post(
      `${modelsEndpoint}/${projectId}/${modelId}/augmentation_preview`,
      getOptions({signalId: 'postAugmentationPreview', data: {augmentation}}),
    )
    .json<AugmentationPreviewResponse>();
};

export const getCombinedPreview = ({
  projectId,
  modelId,
  reactQuerySignal,
}: {
  projectId: string;
  modelId: string;
  reactQuerySignal?: AbortSignal;
}) => {
  return ky
    .get(
      `${modelsEndpoint}/${projectId}/${modelId}/combined_preview`,
      getOptions({signalId: 'getCombinedPreview', reactQuerySignal}),
    )
    .json<CombinedPreviewResponse>();
};

export const getModelInferenceCriteria = (projectId: string, modelId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(
      `${modelsEndpoint}/${projectId}/${modelId}/inference_criteria`,
      getOptions({signalId: 'getModelInferenceCriteria', reactQuerySignal}),
    )
    .json<ModelInferenceCriteriaResponse>();
};

export const putModelInferenceCriteria = ({
  projectId,
  modelId,
  inferenceCriteria,
}: {
  projectId: string;
  modelId: string;
  inferenceCriteria: ModelInferenceCriteriaPayload;
}) => {
  return ky
    .put(
      `${modelsEndpoint}/${projectId}/${modelId}/inference_criteria`,
      getOptions({signalId: 'putModelInferenceCriteria', data: inferenceCriteria}),
    )
    .json<ModelInferenceCriteriaResponse>();
};

export const deleteModelInferenceCriteria = (projectId: string, modelId: string) => {
  return ky
    .delete(
      `${modelsEndpoint}/${projectId}/${modelId}/inference_criteria`,
      getOptions({signalId: 'deleteModelInferenceCriteria'}),
    )
    .json<void>();
};

export const getPreprocessingRois = (projectId: string, modelId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(
      `${modelsEndpoint}/${projectId}/${modelId}/preprocessing_rois`,
      getOptions({
        signalId: 'getPreprocessingRois',
        reactQuerySignal,
      }),
    )
    .json<ModelRoisResponse>();
};

export const getDefectBookStats = (projectId: string, modelId: string, reactQuerySignal?: AbortSignal) => {
  return ky
    .get(
      `${modelsEndpoint}/${projectId}/${modelId}/defect_book_stats`,
      getOptions({
        signalId: 'getDefectBookStats',
        reactQuerySignal,
      }),
    )
    .json<DefectBookStatsResponse>();
};
